import styled from "styled-components";
import { defaultStatusColors, getSpanWithColor } from "../../../utils/CardUtils";
import GridCardComponent from "./generic/GridCardComponent";
import { formatDateByFormat } from "../../../utils/Formatter";
import * as Library from "../../../utils/Library";
import { ReactComponent as Download01 } from "../../../assets/icons/download-01.svg";
import { ReactComponent as Eye } from "../../../assets/icons/eye2.svg";
import { useContext } from "react";
import PopupContext from "../../../PopupContext";

/* #region  SCOPED CSS */
const StyledPage = styled.div``;
/* #endregion */

/**
 * Renders a grid card component for displaying billing invoice information.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.className - The CSS class name for the component.
 * @param {string} props.title - The title of the billing invoice.
 * @param {string} props.id - The ID of the billing invoice.
 * @param {boolean} props.hasBorders - Indicates if the card has borders.
 * @param {string} [props.titlePrefix="STORE"] - The prefix for the title.
 * @param {string} [props.idPrefix="BOTAG ID"] - The prefix for the ID.
 * @param {boolean} props.isCardEnabled - Indicates if the card is enabled.
 * @param {boolean} props.isCardSuspended - Indicates if the card is suspended.
 * @param {string} props.image - The image URL for the billing invoice.
 * @param {Object[]} props.details - The details to display on the card.
 * @param {string} props.details[].key - The key of the detail.
 * @param {string} props.details[].value - The value of the detail.
 * @param {Object} props.edit - The edit button configuration.
 * @param {Object} props.suspend - The suspend button configuration.
 * @param {Object} props.delete - The delete button configuration.
 * @returns {JSX.Element} The rendered grid card component.
 */
export default function BillingInvoicesCard(props) {
    let status = props?.data?.status || "--";
    status = status.charAt(0).toUpperCase() + status.slice(1); // Capitalize the first letter

    const { setGenericConfirmationPopup } = useContext(PopupContext);

    const periodStart = props?.data?.periodStart ? formatDateByFormat(props?.data?.periodStart, "MMM DD") : undefined;
    const periodEnd = props?.data?.periodEnd ? formatDateByFormat(props?.data?.periodEnd, "MMM DD") : undefined;
    const period = periodStart && periodEnd ? `${periodStart} - ${periodEnd}` : "--";
    const dueDate = props?.data?.periodEnd ? formatDateByFormat(props?.data?.periodEnd, "MMM DD - HH:mm") : "--";

    const highlighted = status === "New" || status === "Unpaid";
    const highlightCustomText = status === "New" ? "New" : status === "Unpaid" ? "Unpaid" : undefined;
    const highlightColor =
        status === "New" ? "var(--Buttons-All-Platforms-btn-action-blue, #0060CC)" : "var(--text-icons-red-500, #B00)";

    const titleColor =
        status === "New"
            ? "var(--text-icons-gray-100, #D1D1D1)"
            : status === "Unpaid"
            ? "text-red-500"
            : "var(--text-icons-green-500, #0B0)";

    const PayButtonActive = props?.data?.paymentURL && status !== "Paid" ? true : false;

    const handlePayAction = () => {
        //if (props?.data?.paymentURL) Library.openNewPage(props?.data?.paymentURL);

        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            title: "Redirecting to payment gateway",
            messageHtml:
                "You will be redirected to the payment gateway to complete your payment. <br />Please click REFRESH after payment.",
            confirmText: "PAY",
            cancelText: "REFRESH",
            showCloseButton: false,
            onConfirmClicked: () => {
                if (props?.data?.paymentURL) Library.openNewPage(props?.data?.paymentURL);
            },
            onCancelClicked: () => {
                //window.location.reload();
                const currentUrl = new URL(window.location.href);
                currentUrl.searchParams.set("checkPayment", props?.data?.id);
                window.location.href = currentUrl.toString();
            },
            afterConfirmWillResetPopup: false,
            afterCancelWillResetPopup: false
        });
    };

    const DownloadActive = props?.data?.downloadPDF ? true : false;
    const handleDownloadAction = () => {
        if (props?.data?.downloadPDF) Library.openNewPage(props?.data?.downloadPDF);
    };

    return (
        <StyledPage className={`${props?.className || ""}`}>
            <GridCardComponent
                primaryButton={
                    props?.primaryButton ?? { title: "PAY", onClick: PayButtonActive ? handlePayAction : undefined }
                }
                secondaryButton={props?.secondaryButton}
                title={props?.title}
                id={props?.id}
                hasBorders={props?.hasBorders}
                titlePrefix={props?.titlePrefix || "Company"}
                idPrefix={props?.idPrefix || "BOTAG ID"}
                isCardEnabled={props?.isCardEnabled}
                isCardSuspended={props?.isCardSuspended}
                useTitleLogo={props?.useTitleLogo}
                details={[
                    { key: "Brands:", value: props?.data?.brands || "--" },
                    {
                        key: "Amount:",
                        value:
                            props?.data?.amount !== undefined ? `${props?.data?.amount} ${props?.data?.currency}` : "--"
                    },
                    { key: "Period:", value: period },
                    { key: "Due date:", value: dueDate || "--" },
                    {
                        key: "Status:",
                        value: getSpanWithColor(status ?? "--", defaultStatusColors)
                    }
                ]}
                edit={{ hidden: true }}
                suspend={{ hidden: true }}
                delete={{ hidden: true }}
                view={{ hidden: true }}
                showSelections={true}
                noOfRows={5}
                titleLogoStyles={{ color: titleColor }}
                highlighted={highlighted}
                highlightCustomText={highlightCustomText}
                highlightColor={highlightColor}
                extraRightButtons={[
                    {
                        icon: Eye,
                        title: "View",
                        //onClick: () => {}, //TODO: Implement view action
                        disabled: true
                    },
                    {
                        icon: Download01,
                        title: "Download Invoice",
                        onClick: handleDownloadAction,
                        disabled: !DownloadActive
                    }
                ]}
            />
        </StyledPage>
    );
}
